import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
// import * as S from "./Dashboard/style"
import { getTicketCounts, TicketCountResponse } from '@network/Ticket'
import ReferFriend from '@components/ReferFriend'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '@store/store'
import { EAppAction } from '@store/appReducer'
import { useTiers } from '../../hooks/useTiers'
import { useMilestoneCompletion } from '../../hooks/useMilestoneCompletion'
import { ITier, ITierMilestone } from '../../network/Tier'
import QuestionsModal from '@components/QuestionsModal'
import { trackFunnel } from '@utils/analytics'
import { getUserReferralInfo } from '@network/User/apiUser'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faLock } from '@fortawesome/free-solid-svg-icons'
// import headerIcon from '../../assets/img/headerIcon.svg'
// import HeroRaffle from '@components/DashboardBox/HeroRaffle'


const TicketGenerator = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLoggedIn = useSelector((state: RootState) => state.auth.token);
    const { tiers, loading: tiersLoading, error: tiersError } = useTiers();
    const { isMilestoneCompleted, isMilestoneAvailable, refreshCompletionStatus } = useMilestoneCompletion();
    const [ticketCounts, setTicketCounts] = useState<TicketCountResponse>({
        totalTickets: 0,
        availableTickets: 0,
        usedTickets: 0,
        expiredTickets: 0,
    });
    const [loading, setLoading] = useState(true);
    const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
    const [selectedMilestone, setSelectedMilestone] = useState<{ id: number; title: string } | null>(null);
    const [referralInfo, setReferralInfo] = useState<{ totalInvited: number; monthlyInvited: number; remainingThisMonth: number } | null>(null);
    const user = useSelector((state: RootState) => state.auth.user);

    // Check if profile is completed (firstName, lastName, and email are filled)
    const isProfileCompleted = user?.firstName && user?.lastName && user?.email;

    // const [isMobile, setIsMobile] = useState(false);

    // useEffect(() => {
    //     const checkMobile = () => {
    //         setIsMobile(window.innerWidth < 768);
    //     };

    //     // Set initial value
    //     checkMobile();

    //     // Add resize listener
    //     window.addEventListener('resize', checkMobile);

    //     // Cleanup
    //     return () => window.removeEventListener('resize', checkMobile);
    // }, []);


    useEffect(() => {
        fetchTicketCounts();
        referralCount();
    }, []);



    const fetchTicketCounts = async () => {
        try {
            // setLoading(true);
            const counts = await getTicketCounts();
            setTicketCounts(counts);
        } catch (error) {
            console.error('Error fetching ticket counts:', error);
        } finally {
            setLoading(false);
        }
    };

    const referralCount = async () => {
        try {
            const res = await getUserReferralInfo();
            setReferralInfo(res.data);
        } catch (error) {
            console.error('Error fetching referral count:', error);
        }
    }

    const handleProfileComplete = () => {
        trackFunnel.choiceMade("profile");
        navigate('/profile');
    };

    const handleReferFriend = () => {
        trackFunnel.choiceMade("referral");
        if (!isLoggedIn) {
            // Navigate to login if not logged in
            window.location.href = '/login';
            return;
        }
        dispatch({
            type: EAppAction.SHOW_REFER_POPUP,
            payload: true,
        });
    };

    const handleMilestoneClick = (milestone: { id: number; title: string }) => {
        // Check if milestone is already completed
        if (isMilestoneCompleted(milestone.id)) {
            return; // Don't open modal for completed milestones
        }

        // Check if milestone is available (sequential progression)
        if (!isMilestoneAvailable(milestone.id, tiers)) {
            return; // Don't open modal for unavailable milestones
        }

        setSelectedMilestone(milestone);
        setIsQuestionsModalOpen(true);
        trackFunnel.choiceMade("questions");
    };

    const handleQuestionsModalClose = () => {
        setIsQuestionsModalOpen(false);
        setSelectedMilestone(null);
        // Refresh completion status when modal closes
        refreshCompletionStatus();
    };

    return (
        <>
            {/* Modern Hero Section */}
            <section className="relative bg-gradient-to-br from-[#4B975B] via-[#5dad6d] to-[#4B975B] overflow-hidden md:mt-12">
                {/* Animated Background Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-32 h-32 sm:w-48 sm:h-48 md:w-64 md:h-64 bg-white rounded-full -translate-x-1/2 -translate-y-1/2 animate-pulse"></div>
                    <div className="absolute bottom-0 right-0 w-48 h-48 sm:w-64 sm:h-64 md:w-96 md:h-96 bg-white rounded-full translate-x-1/3 translate-y-1/3 animate-pulse" style={{ animationDelay: '1s' }}></div>
                </div>

                <div className="relative container mx-auto px-4 md:px-6 lg:px-8 py-8 lg:py-12">
                    <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">
                        {/* Left Content */}
                        <div className="flex-1 text-white space-y-4 sm:space-y-5 md:space-y-6">
                            {/* Exciting Badge with Animation */}
                            <div className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-yellow-400/30 to-orange-400/30 backdrop-blur-sm px-4 py-2 sm:px-5 sm:py-3 rounded-full border-2 border-yellow-300/50 shadow-lg animate-pulse">
                                <span className="text-lg sm:text-xl animate-bounce"></span>
                                <span className="text-xs sm:text-sm font-bold uppercase tracking-wide">FREE TICKETS EVERY DAY!</span>
                            </div>

                            {/* Main Heading - More Exciting */}
                            <h1 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-extrabold leading-tight">
                                Start Your Winning Journey!
                                <span className="block mt-2 text-xl md:text-2xl lg:text-3xl xl:text-4xl bg-gradient-to-r from-yellow-200 to-yellow-400 bg-clip-text text-transparent">
                                    Earn Unlimited Free Tickets
                                </span>
                            </h1>

                            {/* Subheading - Clear Value Proposition */}
                            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-white/90 max-w-2xl">
                                Complete your journey through exciting challenges! Answer fun questions, invite friends, and unlock endless free raffle entries for all Dabur competitions.
                                <span className="block mt-2 font-bold text-yellow-300">The more you do, the more you win!</span>
                            </p>

                            {/* Current Tickets Display - Enhanced */}
                            <div className="bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-md rounded-xl sm:rounded-2xl p-4 sm:p-5 md:p-6 border-2 border-white/30 shadow-2xl">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-xs sm:text-sm text-white/90 mb-1 font-semibold">Your Available Tickets</p>
                                        <p className="text-4xl sm:text-5xl font-black text-white drop-shadow-lg">
                                            {loading ? '...' : ticketCounts.availableTickets || 0}
                                        </p>
                                        <p className="text-[10px] sm:text-xs text-yellow-300 mt-1 font-bold animate-pulse">
                                            Ready to use in any raffle!
                                        </p>
                                    </div>
                                    <div className="bg-gradient-to-br from-yellow-400/30 to-orange-400/30 p-3 sm:p-4 md:p-5 rounded-xl sm:rounded-2xl border-2 border-yellow-300/50 shadow-lg">
                                        <svg width="36" height="36" className="sm:w-12 sm:h-12" viewBox="0 0 22 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M19.8575 0.75H16.025C15.8375 0.75 15.68 0.8805 15.6275 1.065C15.485 1.56375 15.0275 1.93275 14.495 1.93275C13.9625 1.93275 13.505 1.572 13.3625 1.065C13.3396 0.976345 13.2884 0.89759 13.2167 0.840726C13.1449 0.783862 13.0565 0.752003 12.965 0.75H2.1425C1.235 0.75 0.5 1.50225 0.5 2.4315V10.3185C0.5 11.2477 1.235 12 2.1425 12H12.965C13.1525 12 13.31 11.8695 13.3625 11.685C13.505 11.1862 13.9625 10.8173 14.495 10.8173C15.0275 10.8173 15.485 11.178 15.6275 11.685C15.68 11.8695 15.8375 12 16.025 12H19.8575C20.765 12 21.5 11.2477 21.5 10.3185V2.4315C21.5 1.50225 20.765 0.75 19.8575 0.75ZM14.495 9.4815C13.8425 9.4815 13.25 8.92425 13.25 8.256C13.25 7.58775 13.8425 7.0545 14.495 7.0545C15.1475 7.0545 15.692 7.58775 15.692 8.256C15.692 8.9235 15.1475 9.4815 14.495 9.4815ZM14.495 5.703C13.8425 5.703 13.259 5.15775 13.259 4.4895C13.259 3.822 13.8425 3.27675 14.495 3.27675C15.1475 3.27675 15.7108 3.822 15.7108 4.4895C15.7108 5.15775 15.1475 5.703 14.495 5.703Z" fill="white" />
                                        </svg>
                                    </div>
                                </div>
                            </div>

                            {/* Enhanced Stats with Icons */}
                            <div className="flex flex-wrap gap-3 sm:gap-4 md:gap-6 pt-4 sm:pt-5 md:pt-6 border-t-2 border-white/30">
                                <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 px-3 py-2 rounded-lg backdrop-blur-sm">
                                    <span className="text-base sm:text-lg"></span>
                                    <span className="text-xs sm:text-sm font-bold">Quick & Easy</span>
                                </div>
                                <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 px-3 py-2 rounded-lg backdrop-blur-sm">
                                    <span className="text-base sm:text-lg"></span>
                                    <span className="text-xs sm:text-sm font-bold">Unlimited Chances</span>
                                </div>
                                <div className="flex items-center gap-1.5 sm:gap-2 bg-white/10 px-3 py-2 rounded-lg backdrop-blur-sm">
                                    <span className="text-base sm:text-lg"></span>
                                    <span className="text-xs sm:text-sm font-bold">Big Prizes</span>
                                </div>
                            </div>

                            {/* Terms */}
                            <p className="text-[10px] sm:text-xs text-white/80 pt-2 bg-white/5 px-3 py-2 rounded-lg">
                                💡 <span className="font-bold">Pro Tip:</span> Complete all 3 ways to maximize your daily tickets! No limits, no catches - just free entries!
                            </p>
                        </div>

                        {/* Right Content - Ticket Visual with Excitement */}
                        <div className="flex-shrink-0 w-full max-w-md lg:max-w-xs lg:w-80 xl:w-96 mx-auto lg:mx-0">
                            <div className="relative">
                                {/* Enhanced Glow Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/30 to-orange-400/30 rounded-full blur-3xl animate-pulse"></div>

                                {/* Ticket Display */}
                                <div className="relative bg-gradient-to-br from-white/15 to-white/5 backdrop-blur-md rounded-2xl sm:rounded-3xl p-6 sm:p-7 md:p-8 border-2 border-white/30 shadow-2xl">
                                    <div className="text-center space-y-3 sm:space-y-4">
                                        <div className="text-6xl sm:text-7xl animate-bounce">🎟️</div>
                                        <div className="text-white">
                                            <p className="text-xs sm:text-sm opacity-90 mb-1 sm:mb-2 font-bold">Total Tickets Earned</p>
                                            <p className="text-5xl sm:text-6xl font-black bg-gradient-to-r from-yellow-200 to-yellow-400 bg-clip-text text-transparent">
                                                {loading ? '...' : ticketCounts.totalTickets || 0}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-3 sm:pt-4 border-t-2 border-white/30">
                                            <div className="bg-white/10 rounded-lg p-2">
                                                <p className="text-[10px] sm:text-xs text-white/80">✅ Used</p>
                                                <p className="text-xl sm:text-2xl font-bold text-white">{ticketCounts.usedTickets || 0}</p>
                                            </div>
                                            <div className="bg-white/10 rounded-lg p-2">
                                                <p className="text-[10px] sm:text-xs text-white/80">⏱️ Expired</p>
                                                <p className="text-xl sm:text-2xl font-bold text-white">{ticketCounts.expiredTickets || 0}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Enhanced Floating Badge */}
                                <div className="absolute -top-4 -right-4 sm:-top-6 sm:-right-6 bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 text-white px-5 py-2.5 sm:px-6 sm:py-3 rounded-full font-black text-sm sm:text-base md:text-lg shadow-2xl border-4 border-white">
                                    <div className="flex items-center gap-1.5 sm:gap-2 animate-pulse">
                                        <span className="text-xl sm:text-2xl"></span>
                                        <span>100% FREE!</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom Wave */}
                <div className="absolute bottom-0 left-0 right-0">
                    <svg className="w-full h-8 sm:h-10 md:h-12 lg:h-16" viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                        <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#EAF0D5" />
                    </svg>
                </div>
            </section>

            <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 sm:py-8">
                {/* Getting Started Guide for First-Time Users */}
                <div className="mb-6 sm:mb-8 bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 border-2 border-purple-200 shadow-lg">
                    <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
                        <div className="flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-lg">
                            <span className="text-2xl sm:text-3xl">🎯</span>
                        </div>
                        <div className="flex-1">
                            <h2 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-black text-gray-800 mb-2">
                                How It Works - Your Journey to FREE Tickets!
                            </h2>
                            <p className="text-sm sm:text-base md:text-lg text-gray-700 mb-3">
                                New here? Welcome! Get started in 3 simple steps and earn tickets instantly:
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                                <div className="bg-white rounded-lg p-3 sm:p-4 shadow-md border border-purple-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg sm:text-xl md:text-2xl">1️⃣</span>
                                        <span className="font-bold text-sm sm:text-base md:text-lg text-purple-700">Complete Profile</span>
                                    </div>
                                    <p className="text-xs sm:text-sm md:text-base text-gray-600">Quick! 1 ticket in 30 seconds</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 sm:p-4 shadow-md border border-purple-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg sm:text-xl md:text-2xl">2️⃣</span>
                                        <span className="font-bold text-sm sm:text-base md:text-lg text-purple-700">Invite Friends</span>
                                    </div>
                                    <p className="text-xs sm:text-sm md:text-base text-gray-600">Up to 30 tickets/month!</p>
                                </div>
                                <div className="bg-white rounded-lg p-3 sm:p-4 shadow-md border border-purple-100">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-lg sm:text-xl md:text-2xl">3️⃣</span>
                                        <span className="font-bold text-sm sm:text-base md:text-lg text-purple-700">Answer Questions</span>
                                    </div>
                                    <p className="text-xs sm:text-sm md:text-base text-gray-600">Unlimited tickets daily!</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Task Cards Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-8">

                    {/* Card 1: Complete Profile */}
                    <div
                        onClick={!isProfileCompleted ? handleProfileComplete : undefined}
                        className={`group relative bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg transition-all duration-300 border-2 border-gray-100 overflow-hidden ${isProfileCompleted
                            ? 'opacity-75 cursor-not-allowed'
                            : 'hover:shadow-2xl cursor-pointer hover:border-[#4B975B]/30 hover:-translate-y-1'
                            }`}
                    >
                        {/* Success Celebration */}
                        {/* {isProfileCompleted && (
                            <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                                ✓ COMPLETED!
                            </div>
                        )} */}

                        {/* Accent Bar */}
                        <div className={`absolute top-0 left-0 w-1.5 sm:w-2 h-full ${isProfileCompleted
                            ? 'bg-gradient-to-b from-green-500 to-green-600'
                            : 'bg-gradient-to-b from-[#4B975B] to-[#5dad6d]'
                            }`}></div>

                        <div className="flex items-start gap-4 mb-4">
                            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg ${isProfileCompleted
                                ? 'bg-gradient-to-br from-green-500 to-green-600'
                                : 'bg-gradient-to-br from-[#4B975B] to-[#5dad6d]'
                                }`}>
                                {isProfileCompleted ? '✓' : '1'}
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">
                                    Complete Your Profile
                                </h3>
                                <p className="text-sm text-gray-600">
                                    {isProfileCompleted
                                        ? "Amazing! You've earned your first ticket"
                                        : 'Takes 30 seconds - Earn 1 FREE ticket instantly!'}
                                </p>
                            </div>
                        </div>

                        <button
                            disabled={isProfileCompleted}
                            className={`w-full py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base transition-all duration-200 flex items-center justify-center gap-2 bg-gradient-to-r from-[#4B975B] to-[#5dad6d] ${isProfileCompleted
                                ? 'opacity-50 cursor-not-allowed'
                                : 'text-white hover:shadow-lg group-hover:scale-105'
                                }`}
                        >
                            <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                            </svg>
                            {isProfileCompleted ? 'Profile Completed ✓' : 'Start Here - Get Your First Ticket!'}
                        </button>

                        {/* Hover Glow Effect */}
                        {!isProfileCompleted && (
                            <div className="absolute -top-20 -right-20 w-40 h-40 bg-[#4B975B]/10 rounded-full blur-3xl group-hover:bg-[#4B975B]/20 transition-all duration-500"></div>
                        )}
                    </div>

                    {/* Card 2: Refer a Friend */}
                    <div
                        onClick={handleReferFriend}
                        className={`group relative bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 shadow-sm transition-all duration-300 border-2 border-gray-200 hover:border-yellow-500 hover:shadow-md cursor-pointer overflow-hidden`}
                    >
                        {/* Success Celebration */}
                        {/* {isProfileCompleted && (
                            <div className="absolute top-2 right-2 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full animate-pulse">
                                ✓ COMPLETED!
                            </div>
                        )} */}

                        {/* Accent Bar */}
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-yellow-400 to-orange-500"></div>

                        <div className="flex items-start gap-4 mb-4">
                            <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-xl flex items-center justify-center text-white font-bold shadow-sm">
                                2
                            </div>
                            <div className="flex-1">
                                <h3 className="text-xl font-bold text-gray-800 mb-1">
                                    Refer Friends
                                </h3>
                                <p className="text-sm text-gray-600">
                                    Share your code & earn up to <span className="font-semibold text-orange-600">30 tickets monthly</span>
                                </p>
                            </div>
                        </div>

                        {/* CTA and Stats */}
                        <div className="space-y-2 sm:space-y-3">
                            <button className="w-full bg-gradient-to-r from-[#4B975B] to-[#5dad6d] text-white py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2 group-hover:scale-105">
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z" />
                                </svg>
                                <span className="hidden xs:inline">🎉 Click To Get Your Referral Code</span>
                                <span className="xs:hidden">Get Code & Share</span>
                            </button>
                            {/* <div className="grid grid-cols-2 gap-2">
                                <div className="text-center text-xs sm:text-sm bg-gradient-to-br from-yellow-50 to-orange-50 py-2 rounded-lg border border-yellow-200">
                                    <div className="font-black text-orange-600 text-lg sm:text-xl">{referralInfo ? referralInfo.monthlyInvited : 0}</div>
                                    <div className="text-[10px] text-gray-600">This Month</div>
                                </div>
                                <div className="text-center text-xs sm:text-sm bg-gradient-to-br from-blue-50 to-purple-50 py-2 rounded-lg border border-purple-200">
                                    <div className="font-black text-purple-600 text-lg sm:text-xl">{referralInfo ? referralInfo.totalInvited : 0}</div>
                                    <div className="text-[10px] text-gray-600">All Time</div>
                                </div>
                            </div> */}
                        </div>

                        {/* Hover Glow Effect */}
                        <div className="absolute -top-20 -right-20 w-40 h-40 bg-yellow-400/10 rounded-full blur-3xl group-hover:bg-yellow-400/20 transition-all duration-500"></div>
                    </div>
                </div>

                {/* Card 3: Answer Questions - Full Width */}
                <div className="relative bg-white rounded-xl sm:rounded-2xl p-5 sm:p-6 md:p-8 shadow-lg border-2 border-gray-100 overflow-hidden space-y-4 sm:space-y-5 md:space-y-6">
                    {/* Accent Bar */}
                    <div className="absolute top-0 left-0 w-1.5 sm:w-2 h-full bg-gradient-to-b from-blue-500 to-indigo-600"></div>

                    {/* Header */}
                    <div className="flex items-start gap-3 sm:gap-4">
                        <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg">
                            3
                        </div>
                        <div className="flex-1">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-[#1f2c2b]">
                                    Complete Your Journey - Answer & Earn!
                                </h3>
                                {/* <span className="inline-flex items-center bg-gradient-to-r from-green-500 to-emerald-600 text-white text-[10px] sm:text-xs font-black px-3 py-1 rounded-full w-fit">
                                    ♾️ UNLIMITED TICKETS!
                                </span> */}
                            </div>
                            <p className="text-xs sm:text-sm md:text-base text-[#6a7b76] mb-2">
                                Progress through exciting levels! Each milestone = more tickets. Complete all challenges to become a champion!
                            </p>

                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3 inline-block">
                        <div className="space-y-2 text-xs sm:text-sm font-bold text-blue-800">
                            <div className="flex items-start gap-2">
                                <span className="text-blue-500">✔</span>
                                <span>Complete milestones in order</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-blue-500">✔</span>
                                <span>Unlock new levels as you progress</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-blue-500">✔</span>
                                <span>Earn tickets instantly!</span>
                            </div>
                        </div>
                    </div>

                    {/* Tiers Content */}
                    {tiersLoading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4B975B]"></div>
                        </div>
                    ) : tiersError ? (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                            <svg className="w-12 h-12 text-red-500 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-red-600 font-medium">Error loading tiers: {tiersError}</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                            {tiers.map((tier: ITier, tierIndex: number) => (
                                <div key={tier.id} className="space-y-3 sm:space-y-4">
                                    {/* Tier Header */}
                                    <div className="bg-gradient-to-r from-indigo-500 via-purple-600 to-purple-800 text-white px-3 py-2 sm:px-4 sm:py-2.5 md:px-5 md:py-3 rounded-lg sm:rounded-xl flex items-center gap-2 sm:gap-3 shadow-md">
                                        <div className="w-6 h-6 sm:w-7 sm:h-7 md:w-8 md:h-8 bg-white rounded-full flex items-center justify-center flex-shrink-0">
                                            <span className="text-purple-700 font-bold text-xs sm:text-sm">{tierIndex + 1}</span>
                                        </div>
                                        <span className="font-bold text-sm sm:text-base">Level {tierIndex + 1}</span>
                                    </div>

                                    {/* Milestone Buttons Grid */}
                                    <div className="bg-gray-50 rounded-lg sm:rounded-xl p-3 sm:p-4">
                                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 sm:gap-3 md:gap-4">
                                            {(() => {
                                                const sortedMilestones = tier.tierMilestones
                                                    ? [...tier.tierMilestones].sort((a, b) => {
                                                        const orderA = a.order ?? 9999;
                                                        const orderB = b.order ?? 9999;
                                                        return orderA - orderB;
                                                    })
                                                    : [];

                                                return sortedMilestones.map((tierMilestone: ITierMilestone, milestoneIndex: number) => {
                                                    const isCompleted = isMilestoneCompleted(tierMilestone.milestone.id);
                                                    const isAvailable = isMilestoneAvailable(tierMilestone.milestone.id, tiers);
                                                    const isLocked = !isAvailable && !isCompleted;
                                                    const displayNumber = tierMilestone.order ?? milestoneIndex + 1;

                                                    return (
                                                        <button
                                                            key={tierMilestone.id}
                                                            onClick={() => handleMilestoneClick({
                                                                id: tierMilestone.milestone.id,
                                                                title: tier.name
                                                            })}
                                                            disabled={isCompleted || isLocked}
                                                            className={`relative w-full aspect-square !rounded-lg sm:!rounded-2xl font-bold text-xs sm:text-sm 
                                                                transition-all duration-200 flex items-center justify-center
                                                                ${isCompleted
                                                                    ? "bg-gradient-to-r from-[#069aad] to-[#7cd859] text-white shadow-md"
                                                                    : isLocked
                                                                        ? "bg-[#b4b4b4] text-gray-400 cursor-not-allowed opacity-50"
                                                                        : "bg-gradient-to-r from-[#034aae] to-[#c46be4] text-white hover:scale-110 hover:shadow-xl cursor-pointer hover:from-[#034aae] hover:to-[#c46be4] animate-pulse"
                                                                }`}
                                                            title={isCompleted
                                                                ? `${tierMilestone.milestone.title} - Completed ✓`
                                                                : isLocked
                                                                    ? `${tierMilestone.milestone.title} - Complete previous milestones first`
                                                                    : `🎯 ${tierMilestone.milestone.title} (${tierMilestone.milestone.numberOfQuestions} questions) - Click to start!`
                                                            }
                                                        >
                                                            <span className="relative z-10 text-2xl font-bold">{displayNumber}</span>
                                                            {isCompleted && (
                                                                <div className="absolute top-1 right-0 w-5 h-5 sm:w-6 sm:h-6 bg-white rounded-full flex items-center justify-center">
                                                                    <FontAwesomeIcon icon={faCheck} className="text-[#7cd859] font-extrabold" />
                                                                </div>
                                                            )}
                                                            {isLocked && (
                                                                <div className="absolute top-1 right-0 w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center">
                                                                    <FontAwesomeIcon icon={faLock} className="text-[#545454]" />
                                                                </div>
                                                            )}
                                                            {!isCompleted && !isLocked && (
                                                                <div className="absolute inset-0 bg-white/20 rounded-md sm:rounded-lg animate-ping opacity-75"></div>
                                                            )}
                                                        </button>
                                                    );
                                                });
                                            })()}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <ReferFriend />
            {selectedMilestone && (
                <QuestionsModal
                    isOpen={isQuestionsModalOpen}
                    onClose={handleQuestionsModalClose}
                    milestoneId={selectedMilestone.id}
                    milestoneTitle={selectedMilestone.title}
                    onSuccess={fetchTicketCounts}
                />
            )}

        </>)
}

export default TicketGenerator